import { useEffect } from 'react';
import { PublicLayout } from '../layout/PublicLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Link, Navigate, useParams } from 'react-router';
import { ArrowRight, Check, Minus, X } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';
import {
  getComparisonBySlug,
  type Comparison,
  type ComparisonLevel,
} from '../data/comparisons';

const levelConfig: Record<
  ComparisonLevel,
  { icon: typeof Check; className: string; label: string }
> = {
  yes: {
    icon: Check,
    className: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400',
    label: 'Strong / built-in',
  },
  partial: {
    icon: Minus,
    className: 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400',
    label: 'Partial — limited or build it yourself',
  },
  no: {
    icon: X,
    className: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-500',
    label: 'Not available',
  },
};

const LevelCell = ({ level, text }: { level: ComparisonLevel; text: string }) => {
  const config = levelConfig[level];
  const Icon = config.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center justify-center w-8 h-8 rounded-full cursor-help ${config.className}`}
          aria-label={config.label}
        >
          <Icon className="h-4 w-4" strokeWidth={3} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-sm">
        <p className="font-semibold mb-1">{config.label}</p>
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  );
};

interface ComparePageProps {
  comparison: Comparison;
}

export const ComparePage = ({ comparison }: ComparePageProps) => {
  const canonicalUrl = `https://raisy.team${comparison.seo.canonicalPath}`;

  useSEO({
    title: comparison.seo.title,
    description: comparison.seo.description,
    keywords: comparison.seo.keywords,
    ogTitle: comparison.seo.title,
    ogDescription: comparison.seo.description,
    ogType: 'article',
    canonicalUrl,
  });

  // FAQPage structured data for SEO rich results
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: comparison.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [comparison]);

  return (
    <PublicLayout>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Header */}
        <div className="mb-12">
          <Badge className="mb-4 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/40">
            {comparison.badge}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            {comparison.headline}
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300">
            {comparison.subheadline}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/request-access">
              <Button className="bg-cyan-600 hover:bg-cyan-700">
                Request Access <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline">How Raisy Works</Button>
            </Link>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-6">
            By the Raisy Team • Last updated {comparison.lastUpdated}
          </p>
        </div>

        {/* At-a-glance comparison table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
            Raisy vs {comparison.competitorName} at a glance
          </h2>
          <TooltipProvider delayDuration={150}>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="w-[52%] font-bold text-slate-900 dark:text-white">
                      Dimension
                    </TableHead>
                    <TableHead className="w-[24%] font-bold text-cyan-700 dark:text-cyan-400 text-center">
                      Raisy
                    </TableHead>
                    <TableHead className="w-[24%] font-bold text-slate-900 dark:text-white text-center">
                      {comparison.competitorName}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparison.dimensions.map((row) => (
                    <TableRow key={row.dimension}>
                      <TableCell className="font-semibold text-slate-900 dark:text-white">
                        {row.dimension}
                      </TableCell>
                      <TableCell className="text-center">
                        <LevelCell level={row.raisyLevel} text={row.raisy} />
                      </TableCell>
                      <TableCell className="text-center">
                        <LevelCell
                          level={row.competitorLevel}
                          text={row.competitor}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TooltipProvider>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-slate-500 dark:text-slate-400">
            {(Object.keys(levelConfig) as ComparisonLevel[]).map((level) => {
              const config = levelConfig[level];
              const Icon = config.icon;
              return (
                <span key={level} className="inline-flex items-center gap-2">
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${config.className}`}
                  >
                    <Icon className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {config.label}
                </span>
              );
            })}
            <span className="italic">Hover or tap any icon for details.</span>
          </div>
        </section>

        {/* Deep dives */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
            The five differences that matter
          </h2>
          <div className="space-y-6">
            {comparison.deepDives.map((dive, index) => (
              <Card
                key={dive.title}
                className="border-slate-200 dark:border-slate-700"
              >
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/30">
                      <dive.icon className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <CardTitle className="text-xl text-slate-900 dark:text-white">
                      {index + 1}. {dive.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="rounded-lg bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-100 dark:border-cyan-900/50 p-4">
                      <p className="font-semibold text-cyan-700 dark:text-cyan-400 mb-1">
                        Raisy
                      </p>
                      <p className="text-slate-700 dark:text-slate-300">
                        {dive.raisy}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4">
                      <p className="font-semibold text-slate-900 dark:text-white mb-1">
                        {comparison.competitorName}
                      </p>
                      <p className="text-slate-600 dark:text-slate-400">
                        {dive.competitor}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Honest fit section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
            Which one is right for you?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900 dark:text-white">
                  When {comparison.competitorName} alone is enough
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {comparison.competitorGoodFit.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-slate-600 dark:text-slate-400"
                    >
                      <X className="h-5 w-5 text-slate-600 dark:text-slate-500 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card className="border-cyan-200 dark:border-cyan-800 bg-cyan-50/50 dark:bg-cyan-950/20">
              <CardHeader>
                <CardTitle className="text-lg text-cyan-700 dark:text-cyan-400">
                  When you need Raisy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {comparison.raisyGoodFit.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-slate-700 dark:text-slate-300"
                    >
                      <Check className="h-5 w-5 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {comparison.faqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`faq-${index}`}>
                <AccordionTrigger className="text-left text-slate-900 dark:text-white">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 dark:text-slate-400">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Bottom CTA */}
        <section className="text-center bg-slate-50 dark:bg-slate-800 rounded-xl p-10 border border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">
            See what a managed AI workforce looks like
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
            Paste your company URL. Raisy builds the processes — you keep the
            oversight.
          </p>
          <Link to="/request-access">
            <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700">
              Request Access <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </section>
      </article>
    </PublicLayout>
  );
};

/**
 * Route wrapper: resolves /compare/:slug to a comparison from the data file.
 * Unknown slugs redirect to the /compare hub.
 */
export const CompareRoute = () => {
  const { slug } = useParams<{ slug: string }>();
  const comparison = slug ? getComparisonBySlug(slug) : undefined;

  if (!comparison) {
    return <Navigate to="/compare" replace />;
  }

  return <ComparePage comparison={comparison} />;
};
